import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid } from 'semantic-ui-react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import FormInput from 'components/FormComponents/FormInput';
import {
  handleSuccess,
  handleError,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  OfferSummary,
  Throttle,
  ThrottleFormData,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';

import UPDATE_NAME_DESCRIPTION_THROTTLE from './UpdateNameAndDescription.mutations';

import styles from '../UpdateThrottle.styles';

type Props = {
  offerUuid: string;
  throttle: Throttle;
  isUpdateNameAndDescriptionFormOpen: (
    value: React.SetStateAction<boolean>,
  ) => void;
  classes: ClassProp<ReturnType<typeof styles>>;
};

type Response = {
  offerGetByUuid: OfferSummary;
};

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Offer Throttle Updated Successfully.',
  },
];

const UpdateNameAndDescription: FunctionComponent<Props> = ({
  offerUuid,
  throttle,
  isUpdateNameAndDescriptionFormOpen,
  classes,
}) => {
  const { uuid, name, description, adjustmentTicketUrl } = throttle;
  const [userName, setUserName] = useState<string>();

  const methods = useForm<ThrottleFormData>({
    mode: 'onBlur',
    defaultValues: {
      uuid,
      name,
      description,
      adjustmentTicketUrl,
    },
  });

  const { register, handleSubmit, errors } = methods;

  const {
    data: { userProfile },
    loading: profileLoading,
  } = useQuery<any>(GET_PROFILE);

  useEffect(() => {
    if (profileLoading) return;
    const { givenName, familyName } = userProfile as Profile;
    setUserName(`${givenName} ${familyName}`);
  }, [userProfile, profileLoading]);

  const handleOfferResponse = () => {
    isUpdateNameAndDescriptionFormOpen(false);
    setIsSubmitting(false);
  };

  const [getOffer] = useLazyQuery<Response>(GET_OFFER, {
    fetchPolicy: 'no-cache',
    onCompleted: handleOfferResponse,
  });

  const handleThrottleUpdateResponse = () => {
    handleSuccess(defaultSuccessMessage);
    getOffer({
      variables: {
        params: {
          offerUuid,
          hydrateFinancialTerms: true,
        },
      },
    });
  };

  useEffect(() => {
    register('name', {
      required: 'The name is required',
      validate: (value) => value !== name || 'You have to edit the name',
    });

    register('description', {
      required: 'The description is required',
      validate: (value) =>
        value !== description || 'You have to edit the description',
    });
  }, []);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [runMutation] = useMutation(UPDATE_NAME_DESCRIPTION_THROTTLE, {
    onCompleted: handleThrottleUpdateResponse,
    onError: handleError,
  });

  const handleClick = () => isUpdateNameAndDescriptionFormOpen(false);

  const onSubmit = ({
    uuid,
    name,
    adjustmentTicketUrl,
    description,
  }: ThrottleFormData) => {
    const currentThrottle = {
      uuid,
      name,
      updatedBy: userName,
      ticketUrl: adjustmentTicketUrl,
      description,
    };

    setIsSubmitting(true);

    runMutation({
      variables: {
        params: { ...currentThrottle },
      },
    });

    return;
  };

  return (
    <Container className={classes.container}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" value={uuid} name="uuid" ref={register} />
          <input
            type="hidden"
            value={adjustmentTicketUrl}
            name="adjustmentTicketUrl"
            ref={register}
          />
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormInput
                  name="name"
                  type="text"
                  placeholder="Enter a name for this throttle"
                  label="Name"
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="description"
                  type="text"
                  placeholder="Brief description for this throttle"
                  label="Description"
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Save'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(UpdateNameAndDescription);
